import { AfterViewInit, Component, computed, ElementRef, inject, Injector, signal, viewChild, ChangeDetectionStrategy } from "@angular/core";
import { CommonButtonRowWidget, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonSelectFieldWidget, CommonTextField, CommonTextFieldWidget, UtilityService } from "@eforall/common";
import { VolunteerInterviewForm, VOLUNTEER_INTERVIEW_RATING_VALUE_MAP } from "@interfaces";
import { ReadonlyAccessWidget } from "../../../readonly-access/readonly-access.widget";
import { VolunteerDialogDataService } from "../volunteer-dialog-data.service";
import { LeadVolunteerDateTimeFormPart } from "./lead-volunteer-date-time-form/lead-volunteer-date-time-form.part";
import { AppService } from "../../../../../services";


@Component({
	selector: 'lead-volunteer-interview-dialog-part',
	standalone: true,
	imports: [
		CommonSelectFieldWidget,
		CommonTextFieldWidget,
		CommonButtonRowWidget,
		LeadVolunteerDateTimeFormPart,
		ReadonlyAccessWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './lead-volunteer-interview-dialog.part.html'
})

export class LeadVolunteerInterviewDialogPart implements AfterViewInit {

	public formSubmitButton = viewChild.required<ElementRef<HTMLButtonElement>>('interviewForm');

	private readonly injector = inject(Injector)
	public readonly dialogDataService = inject(VolunteerDialogDataService);
	private readonly form = new CommonForm<VolunteerInterviewForm>(this.injector);
	private readonly app = inject(AppService);
	private readonly util = inject(UtilityService);

	public readonly isEnabledInterviewForm = computed(() => {
		if (!this.dialogDataService.volunteerDetails()?.volunteerInterview?.volunteerInterviewId) return true;
		if (this.dialogDataService.canManageVolunteerStaff()) return true;
		const staffId = this.dialogDataService.app.data().user.userId;
		const updatedByStaffId = this.dialogDataService.volunteerDetails()?.volunteerInterview?.updatedByStaffId;
		return updatedByStaffId == staffId
	});
	public readonly volunteerInterview = computed(() => {
		const volunteerDetails = this.dialogDataService.volunteerDetails();
		if (volunteerDetails?.volunteerInterview?.volunteerInterviewId) {
			return {
				...volunteerDetails.volunteerInterview,
				updatedByStaffName: this.app.getStaffNameById(volunteerDetails.volunteerInterview.updatedByStaffId),
			};
		}
		return undefined;
	});

	public scoreText = computed(() => {
		const volunteerInterview = this.volunteerInterview();

		if (!volunteerInterview) return `No Interview added for the candidate yet.`;

		const ratings = [
			volunteerInterview.missionAlignmentRating,
			volunteerInterview.experienceRating,
			volunteerInterview.scheduleRating,
			volunteerInterview.mentorshipStyleRating,
			volunteerInterview.presentationStyleRating,
		];

		const sum = this.util.score.calculateInterviewScore(ratings, VOLUNTEER_INTERVIEW_RATING_VALUE_MAP);

		// If three or more ratings are exactly 2 -> disqualify
		// If any rating is 1 -> immediate disqualify
		if (sum == 0) {
			return `The candidate does not qualify based on the interview scoring`;
		}
		else if (sum < 73) {
			return `This candidate got a ${sum}% score in the interview, which does not meet the minimum qualifying score of 73.`;
		}
		else return `This candidate got a ${sum}% score in the interview.`;
	});

	public interviewValues = signal<VolunteerInterviewForm>({
		missionAlignmentRating: 0,
		experienceRating: 0,
		scheduleRating: 0,
		mentorshipStyleRating: 0,
		presentationStyleRating: 0,
		notes: '',
	});

	public readonly hasSubmitButton = computed(() => {
		const initial = this.volunteerInterview();
		if (initial) {
			const current = this.interviewValues();
			return initial.missionAlignmentRating !== current.missionAlignmentRating ||
				initial.experienceRating !== current.experienceRating ||
				initial.scheduleRating !== current.scheduleRating ||
				initial.mentorshipStyleRating !== current.mentorshipStyleRating ||
				initial.presentationStyleRating !== current.presentationStyleRating ||
				initial.notes !== current.notes;
		}
		return false;
	});

	private readonly missionAllignmentOpts = computed(() => [
		{ text: 'Not Suitable', value: 1 },
		{ text: 'Somewhat Suitable', value: 2 },
		{ text: 'Suitable', value: 3 },
		{ text: 'Highly Suitable', value: 4 }
	]);

	readonly missionAlignment = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Mission Alignment', options: this.missionAllignmentOpts(), isRequired: true, isSpanish: false,
			isEnabled: this.isEnabledInterviewForm,

		}),
		actualValue: computed(() => this.interviewValues().missionAlignmentRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.interviewValues();
			this.interviewValues.set({ ...values, missionAlignmentRating: value });
		},
		error: signal(''),
		formFieldName: 'missionAlignmentRating',
		form: this.form,
	}));

	private readonly expertiseAndExperienceOpts = computed(() => [
		{ text: 'Not Qualified', value: 1 },
		{ text: 'Somewhat Qualified', value: 2 },
		{ text: 'Qualified', value: 3 },
		{ text: 'Highly Qualified', value: 4 }
	]);

	readonly expertiseAndExperience = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Expertise and Experience', options: this.expertiseAndExperienceOpts(), isRequired: true, isSpanish: false,
			isEnabled: this.isEnabledInterviewForm,

		}),
		actualValue: computed(() => this.interviewValues().experienceRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.interviewValues();
			this.interviewValues.set({ ...values, experienceRating: value });
		},
		error: signal(''),
		formFieldName: 'experienceRating',
		form: this.form,
	}));

	private readonly scheduleFlexibilityOtps = computed(() => [
		{ text: 'Insufficient schedule flexibility', value: 1 },
		{ text: 'Somewhat sufficient schedule flexibility', value: 2 },
		{ text: 'Sufficient schedule flexibility', value: 3 },
		{ text: 'Highly sufficient schedule flexibility', value: 4 }
	]);

	readonly scheduleFlexibility = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Schedule Flexibility', options: this.scheduleFlexibilityOtps(), isRequired: true, isSpanish: false,
			isEnabled: this.isEnabledInterviewForm,
		}),
		actualValue: computed(() => this.interviewValues().scheduleRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.interviewValues();
			this.interviewValues.set({ ...values, scheduleRating: value });
		},
		error: signal(''),
		formFieldName: 'scheduleRating',
		form: this.form,
	}));

	private readonly mentorshipStyleOpts = computed(() => [
		{ text: 'Observed style not aligned with stated style', value: 1 },
		{ text: 'Observed style somewhat aligned with stated style', value: 2 },
		{ text: 'Observed style aligned with stated style', value: 3 },
		{ text: 'Observed style aligned with stated style and additional styles', value: 4 }
	]);

	readonly mentorshipStyle = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Mentorship Style & Self-Perception', options: this.mentorshipStyleOpts(), isRequired: true, isSpanish: false,
			isEnabled: this.isEnabledInterviewForm,
		}),
		actualValue: computed(() => this.interviewValues().mentorshipStyleRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.interviewValues();
			this.interviewValues.set({ ...values, mentorshipStyleRating: value });
		},
		error: signal(''),
		formFieldName: 'mentorshipStyleRating',
		form: this.form,
	}));

	private readonly presentationStyleOpts = computed(() => [
		{ text: 'Observed presentation style not suitable', value: 1 },
		{ text: 'Observed presentation style somewhat suitable', value: 2 },
		{ text: 'Observed presentation style suitable', value: 3 },
		{ text: 'Observed presentation style highly suitable', value: 4 }
	]);

	readonly presentationStyle = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Presentation Style & Self Perception', options: this.presentationStyleOpts(), isRequired: true,
			isSpanish: false,
			isEnabled: this.isEnabledInterviewForm,
		}),
		actualValue: computed(() => this.interviewValues().presentationStyleRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.interviewValues();
			this.interviewValues.set({ ...values, presentationStyleRating: value });
		},
		error: signal(''),
		formFieldName: 'presentationStyleRating',
		form: this.form,
	}));

	readonly notes = computed<CommonTextField>(() => ({
		config: signal({ label: 'Notes', min: 1, max: 2000, fixCase: 'sentence-case', isRequired: true, multiLine: true, isSpanish: false, isEnabled: this.isEnabledInterviewForm }),
		actualValue: computed(() => this.interviewValues().notes || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		individualSave: async (value: string) => {
			const values = this.interviewValues();
			this.interviewValues.set({ ...values, notes: value });
		},
		error: signal(''),
		formFieldName: 'notes',
		form: this.form,
	}));

	ngAfterViewInit() {
		this.resetForm();
		this.form.setSubmitButton(
			this.formSubmitButton(),
			async (values: VolunteerInterviewForm) => {
				if (!this.dialogDataService.canRecruitVolunteers()) return;
				await this.onSaveInterview(values);
			}
		);
	}


	async onSaveInterview(values: VolunteerInterviewForm): Promise<void> {
		const volunteerId = this.dialogDataService.volunteerDetails()?.volunteerId;
		if (volunteerId) {
			if (this.volunteerInterview() && this.volunteerInterview()?.volunteerInterviewId) {
				await this.dialogDataService.updateVolunteerInterview({
					interviewId: this.volunteerInterview()?.volunteerInterviewId ?? 0,
					interview: values,
					volunteerId: volunteerId
				});
				this.resetForm();
			} else {
				await this.dialogDataService.addVolunteerInterview({ interview: values, volunteerId });
				this.resetForm();
			}
		}
	}

	public resetForm() {
		const volunteerDetails = this.dialogDataService.volunteerDetails();
		if (volunteerDetails?.volunteerInterview?.volunteerInterviewId) {
			const interview: VolunteerInterviewForm = volunteerDetails.volunteerInterview || {
				missionAlignmentRating: 0,
				experienceRating: 0,
				scheduleRating: 0,
				mentorshipStyleRating: 0,
				presentationStyleRating: 0,
				notes: '',
			};
			this.interviewValues.set(interview);
		}
	}

	public getDate(dateUTC: number | undefined) {
		if (!dateUTC) return '';
		return this.util.date.formatUTC(dateUTC, 'DOW MMM D, YYYY', 'H:MM AM EST', 'en-US');
	}
}
